/**
 * Browser-side persistence for the configuration: the URL hash (shareable) and `localStorage` for the unit preference.
 *
 * Precedence on load: URL hash > stored units > defaults — a shared link always
 * wins over a local preference. Hash writes use `replaceState` so dragging a
 * slider never pollutes browser history (§5).
 */
import { DEFAULT_CONFIGURATION, type Configuration, type Units } from "./config";
import { decode, encode } from "./url-state";

const UNITS_KEY = "polar:units";

const readStoredUnits = (): Units | null => {
  try {
    const value = window.localStorage.getItem(UNITS_KEY);
    return value === "metric" || value === "imperial" ? value : null;
  } catch {
    return null; // localStorage unavailable (private mode, blocked)
  }
};

export const hydrateConfiguration = (): Configuration => {
  const storedUnits = readStoredUnits();
  return {
    ...DEFAULT_CONFIGURATION,
    ...(storedUnits !== null ? { units: storedUnits } : {}),
    ...decode(window.location.hash),
  };
};

export const persistConfiguration = (config: Configuration): void => {
  window.history.replaceState(null, "", encode(config));
  try {
    window.localStorage.setItem(UNITS_KEY, config.units);
  } catch {
    // localStorage unavailable — the hash still carries the unit preference.
  }
};
